import 'whatwg-fetch';
import 'navigator.sendbeacon';
import * as urlparse from 'url-parse';
import {ReplaySubject} from 'rxjs';
import cuid from 'cuid';

import {ActivityMonitor} from './activitymonitor';

export class BTDataLayer {
    subject;

    agentId;

    sessionId;

    isFirstPageView;

    pageViewEventId;

    trackerUrl;

    newSession;

    debug;

    sessionCookieName;

    agentCookieName;

    baseDomain;

    sessionTimeoutMinutes;

    activityMonitor;

    constructor({
      subject = new ReplaySubject(),
      cookieManager,
      baseDomain,
      sessionCookieName,
      sessionTimeoutMinutes = 30,
      agentCookieName,
      trackerUrl,
      initialDataLayer = [],
      debug = false,
      fallbackSessionId = null,
      fallbackAgentId = null,
    }) {
      this.baseDomain = baseDomain;
      this.sessionCookieName = sessionCookieName;
      this.sessionTimeoutMinutes = sessionTimeoutMinutes;
      this.agentCookieName = agentCookieName;
      this.trackerUrl = trackerUrl;
      this.subject = subject;
      this.cookieManager = cookieManager;
      this.debug = debug;

      this.isFirstPageView = true;
      this.pageViewEventId = cuid();

      this._initializeSession(fallbackSessionId, fallbackAgentId);

      this.subject.subscribe({next: this._dataHandler});

      initialDataLayer.forEach(val => {
        this.push(val);
      });

      this.activityMonitor = new ActivityMonitor({onActivity: this._updateSessionCookie});
      this.activityMonitor.start();
    }

    push = value => {
      this.subject.next(value);
    };

    _writeLog = (message, level = 'DEBUG') => {
      const pageUrl = urlparse(window.location.href);

      if (level === 'DEBUG' && (this.debug || pageUrl.host !== 'www.biotrust.com')) {
        console.debug(`[cp] ${message}`);
      } else if (level !== 'DEBUG') {
        let logger = null;
        switch (level) {
          case 'WARN':
            logger = console.warn;
            break;
          case 'ERROR':
            logger = console.error;
            break;
          default:
          // eslint-disable-next-line no-console
            logger = console.log;
        }
        logger(message);
      }
    };

    _initializeSession = (fallbackSessionId, fallbackAgentId) => {
      this.sessionId = this.cookieManager.get(this.sessionCookieName) || fallbackSessionId;
      this.agentId = this.cookieManager.get(this.agentCookieName) || fallbackAgentId;
      this.newSession = false;

      if (!this.agentId) {
        this.agentId = cuid();
        this.cookieManager.set(this.agentCookieName, this.agentId, {domain: this.baseDomain, expires: 365});
      }

      if (!this.sessionId) {
        this.sessionId = cuid();
        this.newSession = true;
      }

      this._updateSessionCookie();
      this._writeLog(`BTDataLayer initialized with sessionId=${this.sessionId} agentId=${this.agentId}`);
    };

    _updateSessionCookie = () => {
      const sessionExpirationTime = new Date(new Date().getTime() + this.sessionTimeoutMinutes * 60 * 1000);
      this._writeLog(`Setting session expiration for ${sessionExpirationTime.toTimeString()}`);

      this.cookieManager.set(
        this.sessionCookieName,
        this.sessionId,
        {domain: this.baseDomain, expires: sessionExpirationTime},
      );
    };

    _dataHandler = val => {
      try {
        switch (val.Action) {
          case 'event':
          {
            if (val.Payload) {
              this._sendEvent(val.Payload).then(sent => this._writeLog(`Event tracer sent: ${sent}`));
            } else {
              this._writeLog(`Missing payload for event! ${JSON.stringify(val)}`, 'ERROR');
            }
            break;
          }
          case 'pageview':
          {
            this._sendPageView(val.Payload).then(sent => this._writeLog(`PageView tracer sent: ${sent}`));
            break;
          }
          case 'purchase':
          {
            this._sendConversion(val.Payload).then(sent => this._writeLog(`Conversion tracer sent: ${sent}`));
            break;
          }
          case 'get.session':
          {
            const callback = typeof val.Payload === 'function' ? val.Payload : (val.Payload && val.Payload.callback);
            if (callback) {
              callback({
                sessionId: this.sessionId,
                agentId: this.agentId,
              });
            } else {
              this._writeLog(`Missing or invalid callback for get.session! ${JSON.stringify(val)}`, 'ERROR');
            }
            break;
          }
          case 'get.session.utm':
          {
            const callback = typeof val.Payload === 'function' ? val.Payload : (val.Payload && val.Payload.callback);
            if (callback) {
              fetch(`${this.trackerUrl}session/utm?sessionId=${this.sessionId}`, {method: 'GET'}).then(response => {
                if (!response.ok) {
                  this._writeLog(`Get session UTMs request failed: ${response.status}`, 'ERROR');
                  return null;
                }
                return response.json();
              }).then(callback);
            } else {
              this._writeLog(`Missing or invalid callback for get.session.utm! ${JSON.stringify(val)}`, 'ERROR');
            }
            break;
          }
          default:
          {
            this._writeLog(`Unrecognized action: ${val.Action}`, 'ERROR');
            break;
          }
        }
      } catch (e) {
        this._writeLog(`Error processing data: ${e.message}`, 'ERROR');
      }
    };

    _sendPageView = (payload = {}) => {
      const url = typeof (payload.url) !== 'undefined' ? urlparse(payload.url) : urlparse(window.location.href);
      const queryString = url.query;
      const landingPage = `${url.protocol}//${url.host}${url.pathname}`;
      const referrer = payload.referrer || document.referrer;

      if (this.isFirstPageView) {
        this.isFirstPageView = false;
        if (payload.EventId) {
          this.pageViewEventId = payload.EventId;
        }
      } else {
        this.pageViewEventId = payload.EventId || cuid();
      }

      let tracer = `${this.trackerUrl}session/track`;
      tracer += (queryString.length > 0 ? `${queryString}&` : '?');
      tracer += `referrer=${encodeURIComponent(referrer)}&`;
      tracer += `landingPage=${encodeURIComponent(landingPage)}&`;
      tracer += `eventId=${encodeURIComponent(this.pageViewEventId)}&`;
      tracer += `sessionId=${this.sessionId}&`;
      tracer += `agentId=${this.agentId}`;

      return this._sendData(
        tracer,
        {Create: this.newSession, actualUrl: typeof (payload.url) !== 'undefined' ? window.location.href : null},
      ).then(created => {
        this._writeLog(`PageView Recorded for ${landingPage} and tracer ${tracer}`);
        return created;
      });
    };

    _sendEvent = payload => {
      let tracer = `${this.trackerUrl}session/event?`;
      const eventId = payload.EventId || cuid();

      tracer += `eventId=${encodeURIComponent(eventId)}&`;
      tracer += `pageViewEventId=${this.pageViewEventId}&`;
      tracer += `eventCategory=${payload.EventCategory}&`;
      tracer += `sessionId=${this.sessionId}&`;
      tracer += `agentId=${this.agentId}`;

      return this._sendData(tracer, payload.Properties).then(created => {
        this._writeLog(`Event Recorded... tracer ${eventId}`);
        return created;
      });
    };

    _sendConversion = payload => {
      let tracer = `${this.trackerUrl}session/convert?`;
      const eventId = payload.EventId || cuid();
      tracer += `orderId=${payload.orderId}&`;
      tracer += `through=${payload.through}&`;
      tracer += `amount=${payload.amount}&`;
      tracer += `eventId=${encodeURIComponent(eventId)}&`;
      tracer += `sessionId=${this.sessionId}&`;
      tracer += `agentId=${this.agentId}`;

      const data = payload.properties;

      return this._sendData(tracer, data).then(created => {
        this._writeLog(`Purchase event recorded for orderId ${payload.orderId} and sent to url ${tracer}`);
        return created;
      });
    };

    _sendData = (url, data = '') => {
      // https://xgwang.me/posts/you-may-not-know-beacon/#it-may-throw-error%2C-be-sure-to-catch
      const send = navigator.sendBeacon && navigator.sendBeacon.bind(navigator);
      let ret = false;
      try {
        ret = send(url, JSON.stringify(data));
      } catch (err) {
        console.error(err);
      }
      return Promise.resolve(ret);
    };
}
